import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../components/ui/Button';
import { submitTypingTest } from '../../../api_service';

const TypingTestModal = ({ item, traineeId, onClose, onCompleted }) => {
  const test = item.test;
  const passage = test?.passage || '';
  const duration = test?.durationSeconds || 60;

  const [typed, setTyped] = useState('');
  const [timeLeft, setTimeLeft] = useState(duration);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [finalStats, setFinalStats] = useState(null);

  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const typedRef = useRef('');
  const finishedRef = useRef(false);
  const textareaRef = useRef(null);

  // Keep refs in sync for use inside the timer/finish closures
  useEffect(() => { typedRef.current = typed; }, [typed]);
  useEffect(() => { finishedRef.current = finished; }, [finished]);

  // Cleanup timer on unmount
  useEffect(() => () => clearInterval(intervalRef.current), []);

  const computeStats = (typedText) => {
    let correctChars = 0;
    let errors = 0;
    for (let i = 0; i < typedText.length; i++) {
      if (i < passage.length && typedText[i] === passage[i]) correctChars++;
      else errors++;
    }
    const elapsedMs = startTimeRef.current ? (Date.now() - startTimeRef.current) : 0;
    const elapsedMinutes = elapsedMs / 60000;
    const wpm = elapsedMinutes > 0 ? (correctChars / 5) / elapsedMinutes : 0;
    const accuracy = typedText.length > 0 ? (correctChars / typedText.length) * 100 : 0;
    return {
      wpm: Math.round(wpm),
      accuracy: Math.round(accuracy * 10) / 10,
      charsTyped: typedText.length,
      errors,
    };
  };

  const finish = async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setFinished(true);
    clearInterval(intervalRef.current);

    const stats = computeStats(typedRef.current);
    setFinalStats(stats);
    setSubmitting(true);
    setError('');
    try {
      await submitTypingTest(test.testId, traineeId, stats);
      if (onCompleted) onCompleted();
    } catch {
      setError('Failed to submit result. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const startTimer = () => {
    if (started) return;
    setStarted(true);
    startTimeRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          finish();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChange = (e) => {
    if (finished) return;
    const val = e.target.value;
    if (!started) startTimer();
    setTyped(val);
    // Auto-finish when the full passage has been typed
    if (val.length >= passage.length) {
      finish();
    }
  };

  // Live stats (while typing)
  const live = computeStats(typed);

  // Per-character rendered overlay of the passage
  const renderPassage = () => {
    return passage.split('').map((ch, i) => {
      let cls = 'text-muted-foreground';
      if (i < typed.length) {
        cls = typed[i] === ch ? 'text-success' : 'text-error bg-error/10';
      } else if (i === typed.length && !finished) {
        cls = 'text-foreground bg-primary/20';
      }
      return <span key={i} className={cls}>{ch}</span>;
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{test?.title}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Type the passage as fast and accurately as you can</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">&times;</button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4 flex-1">
          {/* Live metrics */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-muted/40 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-foreground">{timeLeft}s</p>
              <p className="text-xs text-muted-foreground">Time Left</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-primary">{finished ? (finalStats?.wpm ?? 0) : live.wpm}</p>
              <p className="text-xs text-muted-foreground">WPM</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-success">{finished ? (finalStats?.accuracy ?? 0) : live.accuracy}%</p>
              <p className="text-xs text-muted-foreground">Accuracy</p>
            </div>
          </div>

          {/* Passage overlay */}
          <div className="border border-border rounded-lg p-4 bg-background font-mono text-sm leading-relaxed whitespace-pre-wrap break-words">
            {renderPassage()}
          </div>

          {/* Typing area */}
          <textarea
            ref={textareaRef}
            onPaste={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onDrop={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
            className="w-full border border-border rounded-lg p-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none font-mono disabled:opacity-60"
            rows={5}
            value={typed}
            onChange={handleChange}
            disabled={finished}
            autoFocus
            placeholder="Start typing here... the timer starts on your first keystroke."
          />

          {finished && finalStats && (
            <div className="bg-success/5 border border-success/20 rounded-lg p-4">
              <p className="font-semibold text-success mb-2">Test Complete!</p>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div>
                  <p className="text-xl font-bold text-foreground">{finalStats.wpm}</p>
                  <p className="text-xs text-muted-foreground">WPM</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">{finalStats.accuracy}%</p>
                  <p className="text-xs text-muted-foreground">Accuracy</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">{finalStats.charsTyped}</p>
                  <p className="text-xs text-muted-foreground">Chars</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">{finalStats.errors}</p>
                  <p className="text-xs text-muted-foreground">Errors</p>
                </div>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-error">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-border">
          {!finished ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={finish} disabled={!started}>Finish</Button>
            </>
          ) : (
            <Button onClick={onClose} disabled={submitting}>
              {submitting ? 'Saving...' : 'Done'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TypingTestModal;
